import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Button, Card, Row, Col, Spinner, Alert, Modal, Tab, Tabs } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { IMAGE_BASE } from '../Config';
import ProgramData from '../models/ProgramData';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/BaseService';
import { updateProgram } from '../services/ProgramService';
import { validateRequired, validateEmail, validatePhone, validateDescription } from '../utils/validation';

interface EditingProgram {
  id: string;
  field: string;
  value: string;
}

function ManageProgramPage() {
  const [programs, setPrograms] = useState<ProgramData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<ProgramData | null>(null);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form data for editing
  const [formData, setFormData] = useState({
    title: "",
    programCode: "",
    description: "",
    programType: "",
    language: "",
    durationHours: 0,
    durationMinutes: 0,
    email: "",
    phone: "",
    image1: "",
    image2: "",
    image3: ""
  });

  // Related data
  const [programTypes, setProgramTypes] = useState<any[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Validation states
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // File uploads
  const [selectedFiles, setSelectedFiles] = useState<{
    image1: File | null;
    image2: File | null;
    image3: File | null;
  }>({
    image1: null,
    image2: null,
    image3: null
  });

  // Language options
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'te', name: 'Telugu' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'or', name: 'Odia' },
    { code: 'as', name: 'Assamese' }
  ];

  useEffect(() => {
    fetchUserGroups();
    fetchProgramsData(1, 12, true);
    fetchProgramTypes();
  }, []);

  useEffect(() => {
    if (searchQuery !== undefined || selectedGroupId !== undefined) {
      setCurrentPage(1);
      fetchProgramsData(1, 12, true);
    }
  }, [searchQuery, selectedGroupId]);

  const fetchUserGroups = async () => {
    try {
      const response = await apiGet('/programs/user-groups');
      setUserGroups(response.groups || []);
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchProgramTypes = async () => {
    try {
      const data = await apiGet('/programtypes');
      // Exclude Theater Play type
      const filteredTypes = data.filter((type: any) => 
        !type.name.match(/^Theater Play$/i)
      );
      setProgramTypes(filteredTypes);
    } catch (error) {
      console.error('Failed to fetch program types:', error);
    }
  };



  const fetchProgramsData = async (page = 1, limit = 12, reset = false) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedGroupId) params.append('groupId', selectedGroupId);
      
      const response = await apiGet(`/programs/manage-programs?${params.toString()}`);
      const programsData = response.programs || [];
      
      if (reset || page === 1) {
        setPrograms(programsData);
      } else {
        setPrograms(prev => [...prev, ...programsData]);
      }
      
      setHasMore(response.hasMore || false);
      setTotalPrograms(response.totalPrograms || 0);
      setCurrentPage(response.currentPage || page);
    } catch (error) {
      setError('Failed to fetch programs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchProgramGroups = async (programId: string) => {
    try {
      const data = await apiGet(`/programgroups/program/${programId}`);
      const groupIds = data.map((pg: any) => pg.groupId);
      setSelectedGroupIds(groupIds);
    } catch (error) {
      setSelectedGroupIds([]);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    fetchProgramsData(nextPage, 12, false);
  };

  const generateProgramCode = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PROG-${timestamp}-${random}`;
  };

  const handleShowModal = (program?: ProgramData) => {
    if (program) {
      // Edit mode
      setEditingProgram(program);
      setFormData({
        title: program.title || "",
        programCode: program.programCode || "",
        description: program.description || "",
        programType: program.programType || "",
        language: program.language || "",
        durationHours: program.durationHours || 0,
        durationMinutes: program.durationMinutes || 0,
        email: program.email || "",
        phone: program.phone || "",
        image1: program.image1 || "",
        image2: program.image2 || "",
        image3: program.image3 || ""
      });
      
      // Fetch existing relationships
      fetchProgramGroups(program._id!);
    } else {
      // Add mode
      setEditingProgram(null);
      setFormData({
        title: "",
        programCode: generateProgramCode(),
        description: "",
        programType: programTypes.length > 0 ? programTypes[0]._id : "",
        language: "",
        durationHours: 0,
        durationMinutes: 0,
        email: "",
        phone: "",
        image1: "",
        image2: "",
        image3: ""
      });
      
      // Reset relationships
      setSelectedGroupIds(userGroups.length === 1 ? [userGroups[0]._id] : []);
    }
    
    setSelectedFiles({ image1: null, image2: null, image3: null });
    setValidationErrors({});
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (imageKey: 'image1' | 'image2' | 'image3') => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFiles(prev => ({ ...prev, [imageKey]: e.target.files![0] }));
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!file) return null;
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const result = await apiPost('/image/upload/theaterPlayImage', formDataUpload);
      return `/theaterPlayImage/${result.fileName}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };



  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    const titleValidation = validateRequired(formData.title, 'Program Title');
    if (!titleValidation.isValid) {
      errors.title = titleValidation.message!;
    }
    
    const programCodeValidation = validateRequired(formData.programCode, 'Program Code');
    if (!programCodeValidation.isValid) {
      errors.programCode = programCodeValidation.message!;
    }
    
    const programTypeValidation = validateRequired(formData.programType, 'Program Type');
    if (!programTypeValidation.isValid) {
      errors.programType = programTypeValidation.message!;
    }
    
    if (selectedGroupIds.length === 0) {
      errors.group = 'Group selection is required';
    }
    
    if (formData.email) {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.message!;
      }
    }
    
    if (formData.phone) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.isValid) {
        errors.phone = phoneValidation.message!;
      }
    }
    
    const descriptionValidation = validateDescription(formData.description);
    if (!descriptionValidation.isValid) {
      errors.description = descriptionValidation.message!;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    try {
      setUploading(true);
      
      // Upload images
      const imageUploads = await Promise.all([
        selectedFiles.image1 ? uploadImage(selectedFiles.image1) : formData.image1,
        selectedFiles.image2 ? uploadImage(selectedFiles.image2) : formData.image2,
        selectedFiles.image3 ? uploadImage(selectedFiles.image3) : formData.image3
      ]);

      const submitData = {
        ...formData,
        image1: imageUploads[0] || "",
        image2: imageUploads[1] || "",
        image3: imageUploads[2] || ""
      };

      // Create or Update Program
      const savedProgram = editingProgram 
        ? await updateProgram(editingProgram._id!, submitData as Partial<ProgramData>)
        : await apiPost('/programs', submitData);
      
      const programId = savedProgram._id || editingProgram?._id;

      // Save Program Groups
      if (selectedGroupIds.length > 0) {
        try {
          await Promise.all(selectedGroupIds.map(groupId => 
            apiPost('/programgroups', { programId, groupId })
          ));
        } catch (error) {
          console.error('Failed to save program groups:', error);
        }
      }
      
      setSuccess(editingProgram ? "Program updated successfully" : "Program created successfully");
      await fetchProgramsData(1, 12, true);
      setShowModal(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(editingProgram ? "Failed to update program" : "Failed to create program");
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (programId: string) => {
    if (window.confirm("Are you sure you want to delete this program?")) {
      try {
        await apiDelete(`/programs/${programId}`);
        setSuccess("Program deleted successfully");
        await fetchProgramsData(1, 12, true);
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        setError("Failed to delete program");
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  return (
    <PermissionWrapper pageName="ManageProgramPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h3 className="mb-3 mb-md-0">
                <i className="bi bi-collection me-2"></i>
                Manage My Programs
              </h3>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Program
              </Button>
            </div>
          </Col>
        </Row>

        {/* Search and Filter Section */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Search & Filter</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Select 
                  value={selectedGroupId} 
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                >
                  <option value="">All My Groups</option>
                  {userGroups.map(group => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Search programs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="me-2"
                  />
                  <Button variant="outline-secondary" onClick={() => handleSearch(searchQuery)}>
                    <i className="bi bi-search"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="text-muted">
            {programs.length > 0 ? `Showing ${programs.length} programs` : "No programs found"}
            {searchQuery && ` for "${searchQuery}"`}
            {selectedGroupId && userGroups.find(g => g._id === selectedGroupId) && 
              ` from group "${userGroups.find(g => g._id === selectedGroupId)?.name}"`
            }
          </small>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {programs.length === 0 ? (
              <Card>
                <Card.Body className="text-center py-5">
                  <i className="bi bi-collection" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  <h4 className="mt-3 text-muted">No Programs Found</h4>
                  <p className="text-muted">
                    {userGroups.length === 0 
                      ? "You don't have any groups yet. Create a group first to add programs."
                      : "You don't have any programs (excluding plays) associated with your groups yet."
                    }
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-3">
                {programs.map((program) => {
                  return (
                    <Col xs={12} sm={6} md={4} lg={3} key={program._id}>
                      <Card className="h-100 program-card">
                        <div className="program-image-container">
                          {program.image1 ? (
                            <Card.Img 
                              variant="top" 
                              src={IMAGE_BASE + program.image1}
                              className="program-image"
                              style={{ height: '200px', objectFit: 'cover' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                              }}
                            />
                          ) : (
                            <div className="program-image-placeholder" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                              <i className="bi bi-collection display-4 text-muted"></i>
                            </div>
                          )}
                        </div>
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="h6">{program.title}</Card.Title>
                          <Card.Text className="small text-muted flex-grow-1">
                            {program.description ? 
                              (program.description.length > 80 ? 
                                program.description.substring(0, 80) + '...' : 
                                program.description
                              ) : 'No description available'
                            }
                          </Card.Text>
                          <div className="program-info mb-2">
                            <div className="small text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {formatDuration(program.durationHours, program.durationMinutes)}
                            </div>
                            {program.language && (
                              <div className="small text-muted">
                                <i className="bi bi-translate me-1"></i>
                                {program.language}
                              </div>
                            )}
                          </div>
                          <div className="d-flex gap-1 mt-auto">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(program)}
                              className="flex-fill"
                            >
                              <i className="bi bi-pencil me-1"></i>
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(program._id!)}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            
            {hasMore && (
              <div className="text-center mt-4">
                <Button 
                  variant="outline-primary" 
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Add/Edit Program Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{editingProgram ? "Edit Program" : "Add Program"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Tabs defaultActiveKey="basic" className="mb-3">
                <Tab eventKey="basic" title="Basic Info">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Title <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          isInvalid={!!validationErrors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.title}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Code <span className="text-danger">*</span></Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            name="programCode"
                            value={formData.programCode}
                            onChange={handleInputChange}
                            required
                            readOnly={!!editingProgram}
                            isInvalid={!!validationErrors.programCode}
                          />
                          {!editingProgram && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setFormData(prev => ({ ...prev, programCode: generateProgramCode() }))}
                              className="ms-2"
                              type="button"
                            >
                              <i className="bi bi-arrow-clockwise"></i>
                            </Button>
                          )}
                        </div>
                        {validationErrors.programCode && (
                          <div className="invalid-feedback d-block">
                            {validationErrors.programCode}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Type <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="programType"
                          value={formData.programType}
                          onChange={handleInputChange}
                          required
                          isInvalid={!!validationErrors.programType}
                        >
                          <option value="">Select Program Type</option>
                          {programTypes.map(type => (
                            <option key={type._id} value={type._id}>
                              {type.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.programType}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Language</Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Language</option>
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Group <span className="text-danger">*</span></Form.Label>
                        {userGroups.length === 1 ? (
                          <Form.Control
                            type="text"
                            value={userGroups[0].name}
                            readOnly
                            className="bg-light"
                          />
                        ) : (
                          <Form.Select
                            value={selectedGroupIds[0] || ""}
                            onChange={(e) => {
                              setSelectedGroupIds(e.target.value ? [e.target.value] : []);
                              if (validationErrors.group) {
                                setValidationErrors(prev => ({ ...prev, group: '' }));
                              }
                            }}
                            required
                            isInvalid={!!validationErrors.group}
                          >
                            <option value="">Select Group</option>
                            {userGroups.map(group => (
                              <option key={group._id} value={group._id}>
                                {group.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                        {validationErrors.group && (
                          <div className="invalid-feedback d-block">
                            {validationErrors.group}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Hours</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationHours"
                          value={formData.durationHours}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Minutes</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationMinutes"
                          value={formData.durationMinutes}
                          onChange={handleInputChange}
                          min="0"
                          max="59"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          isInvalid={!!validationErrors.phone}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.phone}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.description}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Tab>

                <Tab eventKey="images" title="Images">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 1</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image1')}
                        />
                        {formData.image1 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image1}
                              alt="Image 1"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 2</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image2')}
                        />
                        {formData.image2 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image2}
                              alt="Image 2"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 3</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image3')}
                        />
                        {formData.image3 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image3}
                              alt="Image 3"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>


              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingProgram ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>{editingProgram ? "Update" : "Create"} Program</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </PermissionWrapper>
  );
}

export default ManageProgramPage;